import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { workoutPlanService, exerciseService } from '../../services/supabaseService';
import ExerciseLibraryPanel from './components/ExerciseLibraryPanel';
import WorkoutConstructionArea from './components/WorkoutConstructionArea';
import SettingsPanel from './components/SettingsPanel';
import WorkoutPlansList from './components/WorkoutPlansList';
import SaveWorkoutModal from './components/SaveWorkoutModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import AIWorkoutRecommendations from './components/AIWorkoutRecommendations';

const WorkoutBuilder = () => {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [selectedExerciseIndex, setSelectedExerciseIndex] = useState(null);
  const [workoutMetadata, setWorkoutMetadata] = useState({
    name: '',
    description: '',
    difficulty: 'beginner',
    tags: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activePanel, setActivePanel] = useState('library');

  useEffect(() => {
    loadData();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [exercisesRes, plansRes] = await Promise.all([
        exerciseService?.getExercises(),
        workoutPlanService?.getWorkoutPlans(user?.id)
      ]);
      setExercises(exercisesRes?.data || []);
      setWorkoutPlans(plansRes?.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExercise = (exercise) => {
    setSelectedExercises(prev => [
      ...prev,
      {
        id: `${exercise?.id}-${Date.now()}`,
        exerciseId: exercise?.id,
        name: exercise?.name,
        category: exercise?.category,
        muscleGroups: exercise?.muscleGroups,
        sets: 3,
        reps: 10,
        durationSeconds: null,
        restSeconds: 60,
        notes: '',
        tempo: '2-0-2-0'
      }
    ]);
  };

  const handleRemoveExercise = (index) => {
    setSelectedExercises(prev => prev?.filter((_, i) => i !== index));
    if (selectedExerciseIndex === index) {
      setSelectedExerciseIndex(null);
    }
  };

  const handleReorderExercises = (newOrder) => {
    setSelectedExercises(newOrder);
  };

  const handleSelectExercise = (index) => {
    setSelectedExerciseIndex(index);
    if (isMobile) {
      setActivePanel('settings');
    }
  };

  const handleUpdateExercise = (index, updates) => {
    setSelectedExercises(prev => {
      const updated = [...prev];
      updated[index] = { ...updated?.[index], ...updates };
      return updated;
    });
  };

  const calculateTotalDuration = () => {
    return selectedExercises?.reduce((total, ex) => {
      const exerciseTime = ex?.durationSeconds || (ex?.sets * ex?.reps * 3);
      const restTime = ex?.restSeconds * (ex?.sets - 1);
      return total + exerciseTime + restTime;
    }, 0);
  };

  const handleSaveWorkout = async (metadata) => {
    try {
      const totalDuration = Math.ceil(calculateTotalDuration() / 60);
      const planData = {
        ...metadata,
        durationMinutes: totalDuration,
        isTemplate: true
      };

      if (editingPlanId) {
        await workoutPlanService?.updateWorkoutPlan(editingPlanId, planData, selectedExercises);
      } else {
        await workoutPlanService?.createWorkoutPlan(planData, selectedExercises);
      }

      setShowSaveModal(false);
      handleNewWorkout();
      loadData();
    } catch (error) {
      console.error('Error saving workout:', error);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlanId(plan?.id);
    setWorkoutMetadata({
      name: plan?.name,
      description: plan?.description,
      difficulty: plan?.difficulty,
      tags: plan?.tags || []
    });
    setSelectedExercises(
      plan?.workoutPlanExercises?.map((ex, idx) => ({
        id: `${ex?.exerciseId}-${idx}`,
        exerciseId: ex?.exerciseId,
        name: ex?.exercise?.name,
        category: ex?.exercise?.category,
        muscleGroups: ex?.exercise?.muscleGroups,
        sets: ex?.sets,
        reps: ex?.reps,
        durationSeconds: ex?.durationSeconds,
        restSeconds: ex?.restSeconds,
        notes: ex?.notes,
        tempo: '2-0-2-0'
      })) || []
    );
    setSelectedExerciseIndex(null);
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    try {
      await workoutPlanService?.deleteWorkoutPlan(planToDelete?.id);
      setShowDeleteModal(false);
      setPlanToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  const handleNewWorkout = () => {
    setEditingPlanId(null);
    setSelectedExercises([]);
    setSelectedExerciseIndex(null);
    setWorkoutMetadata({
      name: '',
      description: '',
      difficulty: 'beginner',
      tags: []
    });
  };

  const totalDurationMinutes = Math.ceil(calculateTotalDuration() / 60);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Workout Builder - Form Forge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
                {editingPlanId ? 'Edit Workout Plan' : 'Workout Builder'}
              </h1>
              <p className="mt-2" style={{ color: '#6B7280' }}>
                Create and customize comprehensive workout plans with drag-and-drop exercise sequencing
              </p>
            </div>
            <div className="flex gap-3">
              {editingPlanId && (
                <Button variant="outline" onClick={handleNewWorkout}>
                  <Icon name="Plus" size={18} className="mr-2" />
                  New Workout
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowAIRecommendations(true)}>
                <Icon name="Sparkles" size={18} className="mr-2" />
                AI Recommendations
              </Button>
              <Button onClick={() => setShowSaveModal(true)} disabled={selectedExercises?.length === 0}>
                <Icon name="Save" size={18} className="mr-2" />
                {editingPlanId ? 'Update' : 'Save'} Workout
              </Button>
            </div>
          </div>

          {isMobile ? (
            <div className="space-y-4">
              <div className="flex gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  { id: 'library', icon: 'Library', label: 'Library' },
                  { id: 'workout', icon: 'List', label: `Workout (${selectedExercises?.length})` },
                  { id: 'settings', icon: 'Settings', label: 'Settings' },
                  { id: 'plans', icon: 'FolderOpen', label: 'Plans' }
                ]?.map(panel => (
                  <button key={panel?.id} onClick={() => setActivePanel(panel?.id)} className="flex-1 py-3 text-sm font-medium transition-colors" style={activePanel === panel?.id ? { color: '#FF6B00', borderBottom: '2px solid #FF6B00' } : { color: '#6B7280' }}>
                    <Icon name={panel?.icon} size={16} className="inline mr-1" />{panel?.label}
                  </button>
                ))}
              </div>
              {activePanel === 'library' && <ExerciseLibraryPanel exercises={exercises} onAddExercise={handleAddExercise} selectedExerciseIds={selectedExercises?.map(e => e?.exerciseId)} />}
              {activePanel === 'workout' && <WorkoutConstructionArea exercises={selectedExercises} onReorder={handleReorderExercises} onRemove={handleRemoveExercise} onSelect={handleSelectExercise} selectedIndex={selectedExerciseIndex} totalDuration={totalDurationMinutes} />}
              {activePanel === 'settings' && <SettingsPanel exercise={selectedExercises?.[selectedExerciseIndex]} onUpdate={(updates) => handleUpdateExercise(selectedExerciseIndex, updates)} isActive={selectedExerciseIndex !== null} />}
              {activePanel === 'plans' && <WorkoutPlansList plans={workoutPlans} onEdit={handleEditPlan} onDelete={(plan) => { setPlanToDelete(plan); setShowDeleteModal(true); }} currentPlanId={editingPlanId} />}
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <ExerciseLibraryPanel exercises={exercises} onAddExercise={handleAddExercise} selectedExerciseIds={selectedExercises?.map(e => e?.exerciseId)} />
                <div className="mt-6">
                  <WorkoutPlansList plans={workoutPlans} onEdit={handleEditPlan} onDelete={(plan) => { setPlanToDelete(plan); setShowDeleteModal(true); }} currentPlanId={editingPlanId} />
                </div>
              </div>
              <div className="col-span-6">
                <WorkoutConstructionArea exercises={selectedExercises} onReorder={handleReorderExercises} onRemove={handleRemoveExercise} onSelect={handleSelectExercise} selectedIndex={selectedExerciseIndex} totalDuration={totalDurationMinutes} />
              </div>
              <div className="col-span-3">
                <SettingsPanel exercise={selectedExercises?.[selectedExerciseIndex]} onUpdate={(updates) => handleUpdateExercise(selectedExerciseIndex, updates)} isActive={selectedExerciseIndex !== null} />
              </div>
            </div>
          )}
        </main>
      </div>
      {showSaveModal && (
        <SaveWorkoutModal
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveWorkout}
          initialData={workoutMetadata}
          isEditing={!!editingPlanId}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          onClose={() => {
            setShowDeleteModal(false);
            setPlanToDelete(null);
          }}
          onConfirm={handleDeletePlan}
          planName={planToDelete?.name}
        />
      )}
      {showAIRecommendations && (
        <AIWorkoutRecommendations
          currentWorkoutPlan={editingPlanId ? {
            name: workoutMetadata?.name,
            exercises: selectedExercises
          } : null}
          onClose={() => setShowAIRecommendations(false)}
        />
      )}
    </>
  );
};

export default WorkoutBuilder;